import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { toastSuccess, toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import { soulExampleKeys, useSoulExamples } from '@Hooks/useSoulExamples';

interface Props {
  soulId: string;
}

export default function SoulExamplesCard({ soulId }: Props) {
  const queryClient = useQueryClient();
  const { data: examples, status } = useSoulExamples({ soulId });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [expectedResponse, setExpectedResponse] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: soulExampleKeys.forSoul(soulId) });

  const reset = () => {
    setName('');
    setUserMessage('');
    setExpectedResponse('');
    setNotes('');
  };

  const save = async () => {
    if (!userMessage.trim() || !expectedResponse.trim()) {
      toastError('User message and expected response are required.');
      return;
    }
    setSaving(true);
    try {
      await service.createSoulExample(soulId, {
        name: name || null,
        inputMessages: [{ role: 'user', content: userMessage }],
        expectedResponse,
        notes: notes || null,
        active: true,
      });
      await invalidate();
      reset();
      setOpen(false);
      toastSuccess('Example added.');
    } catch (e: any) {
      toastError(`Failed: ${e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      await service.updateSoulExample(id, { active: !currentActive });
      await invalidate();
    } catch (e: any) {
      toastError(`Toggle failed: ${e?.message ?? e}`);
    }
  };

  const remove = async (id: string) => {
    try {
      await service.deleteSoulExample(id);
      await invalidate();
      toastSuccess('Example removed.');
    } catch (e: any) {
      toastError(`Delete failed: ${e?.message ?? e}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <Heading level="h3">Examples</Heading>
          <p className="text-xs text-zinc-500">
            Few-shot examples injected into this soul's system prompt. Editing them invalidates the prompt cache on next chat.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline">Add Example</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Example</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
              <Textarea
                placeholder="User message"
                rows={3}
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
              />
              <Textarea
                placeholder="Expected response"
                rows={5}
                value={expectedResponse}
                onChange={(e) => setExpectedResponse(e.target.value)}
              />
              <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="link">Cancel</Button>
              </DialogClose>
              <Button onClick={save} loading={saving}>
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {status === 'pending' ? (
          <div className="py-4 text-sm text-zinc-500">Loading…</div>
        ) : !examples?.length ? (
          <div className="py-4 text-sm text-zinc-500">No examples yet.</div>
        ) : (
          <div className="space-y-3">
            {examples.map((ex) => {
              const firstMsg = Array.isArray(ex.inputMessages) ? ex.inputMessages[0] : ex.inputMessages;
              const userPreview = (firstMsg?.content ?? '').toString().slice(0, 80);
              return (
                <div key={ex.id} className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <strong>{ex.name ?? 'Untitled'}</strong>
                      <Badge variant={ex.active ? 'default' : 'outline'}>
                        {ex.active ? 'active' : 'inactive'}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="link" size="sm" onClick={() => toggleActive(ex.id, ex.active)}>
                        {ex.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="dangerLink" size="sm" onClick={() => remove(ex.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500 mb-1">Input: {userPreview}{userPreview.length === 80 ? '…' : ''}</p>
                  <p className="text-xs">{ex.expectedResponse.slice(0, 200)}{ex.expectedResponse.length > 200 ? '…' : ''}</p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
